import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatNumber } from '../lib/format';
import {
  MONITOR_SEVERITIES,
  headersToText,
  intervalText,
  kindLabel,
  severityLabel,
  textToHeaders,
  useMonitoringDefaults,
  useServerOptions,
  type MonitorKind,
  type MonitorMethod,
  type MonitorSeverity,
  type UrlDetail,
} from '../lib/monitoring';
import { FormGrid, FormPage } from '../components/FormPage';
import { Field, Input, Select, Textarea, useToast } from '../components/ui';

type Figure = 'intervalSeconds' | 'timeoutSeconds' | 'failuresToConfirm' | 'slowMs' | 'sslWarnDays';

/**
 * Routes: `/monitoring/urls/new` (`?serverId=` when added from a server) and
 * `/monitoring/urls/:id/edit`. A URL is on one server, and may name one of that
 * server's projects or none (operator, 2026-10-20: "when add url may i add
 * projects or not from server_projects"). Each check figure left empty takes
 * the Monitoring default.
 */
export function MonitoringUrlFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const [search] = useSearchParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const servers = useServerOptions();
  const defaults = useMonitoringDefaults();

  const existing = useQuery({
    queryKey: ['monitoring', 'url', id],
    queryFn: () => api.get<UrlDetail>(`/monitoring/urls/${id}`),
    enabled: editing,
  });

  const [form, setForm] = useState({
    serverId: search.get('serverId') ?? '',
    projectId: '',
    name: '',
    kind: 'HTTP' as MonitorKind,
    url: '',
    method: 'GET' as MonitorMethod,
    expectedStatus: '',
    keyword: '',
    headers: '',
    body: '',
    host: '',
    port: '',
    severity: 'MEDIUM' as MonitorSeverity,
  });
  const [figures, setFigures] = useState<Record<Figure, string>>({
    intervalSeconds: '',
    timeoutSeconds: '',
    failuresToConfirm: '',
    slowMs: '',
    sslWarnDays: '',
  });

  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    setLoaded(true);
    const u = existing.data;
    setForm({
      serverId: u.serverId,
      projectId: u.projectId ?? '',
      name: u.name,
      kind: u.kind,
      url: u.kind === 'HTTP' ? u.url : '',
      method: u.method,
      expectedStatus: u.expectedStatus ?? '',
      keyword: u.keyword ?? '',
      headers: headersToText(u.headers),
      body: u.body ?? '',
      host: u.host ?? '',
      port: u.port === null ? '' : String(u.port),
      severity: u.severity,
    });
    const own = (value: number | null) => (value === null ? '' : String(value));
    setFigures({
      intervalSeconds: own(u.intervalSeconds),
      timeoutSeconds: own(u.timeoutSeconds),
      failuresToConfirm: own(u.failuresToConfirm),
      slowMs: own(u.slowMs),
      sslWarnDays: own(u.sslWarnDays),
    });
  }

  const server = (servers.data ?? []).find((s) => s.id === form.serverId);
  // The server's projects that may be picked, and the one the URL has if it was deleted since.
  const projectChoices = (server?.projects ?? []).filter((p) => !p.deleted || p.id === form.projectId);

  // Moved to another server, a URL keeps its project only if that server runs it too.
  const pickServer = (serverId: string) => {
    const next = (servers.data ?? []).find((s) => s.id === serverId);
    const keeps = !!next?.projects.some((p) => p.id === form.projectId && !p.deleted);
    setForm({ ...form, serverId, projectId: keeps ? form.projectId : '' });
  };

  // An address typed with no name yet names the URL after its host.
  const nameFromAddress = () => {
    if (form.name.trim() || !form.url.trim()) return;
    try {
      const text = /^[a-z][a-z0-9+.-]*:\/\//i.test(form.url.trim()) ? form.url.trim() : `https://${form.url.trim()}`;
      setForm((f) => ({ ...f, name: new URL(text).hostname }));
    } catch {
      // Not an address yet: the API says what is wrong with it on save.
    }
  };

  const isHttp = form.kind === 'HTTP';
  const d = defaults.data;
  const placeholder = (value: number | undefined, show: (v: number) => string) =>
    value === undefined ? '' : t('monitoring.url.defaultIs', { value: show(value) });
  const number = (key: Figure) => (figures[key] === '' ? null : Number(figures[key]));

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        serverId: form.serverId,
        projectId: form.projectId || null,
        name: form.name.trim(),
        kind: form.kind,
        severity: form.severity,
        intervalSeconds: number('intervalSeconds'),
        timeoutSeconds: number('timeoutSeconds'),
        failuresToConfirm: number('failuresToConfirm'),
        slowMs: isHttp ? number('slowMs') : null,
        sslWarnDays: isHttp ? number('sslWarnDays') : null,
        ...(isHttp
          ? {
              url: form.url,
              method: form.method,
              expectedStatus: form.expectedStatus || null,
              keyword: form.keyword || null,
              headers: textToHeaders(form.headers),
              body: form.method === 'POST' ? form.body || null : null,
            }
          : { host: form.host, port: form.port === '' ? undefined : Number(form.port) }),
      };
      return editing ? api.patch<UrlDetail>(`/monitoring/urls/${id}`, body) : api.post<UrlDetail>('/monitoring/urls', body);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['monitoring'] });
      navigate(`/monitoring/urls/${saved.id ?? id}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const ready =
    !!form.serverId && !!form.name.trim() && (isHttp ? !!form.url.trim() : !!form.host.trim() && form.port !== '');
  const backTo = editing ? `/monitoring/urls/${id}` : search.get('serverId') ? `/monitoring/servers/${search.get('serverId')}` : '/monitoring/urls';

  return (
    <FormPage
      title={editing ? `${t('common.edit')} — ${existing.data?.name ?? ''}` : t('monitoring.urls.new')}
      backTo={backTo}
      backLabel={editing ? (existing.data?.name ?? t('monitoring.urls.title')) : server && search.get('serverId') ? server.name : t('monitoring.urls.title')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={!ready}
      loading={(editing && existing.isLoading) || servers.isLoading}
      error={existing.error ?? servers.error}
      onRetry={() => {
        void existing.refetch();
        void servers.refetch();
      }}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={t('monitoring.url.server')} required>
          <Select value={form.serverId} onChange={(e) => pickServer(e.target.value)} autoFocus={!form.serverId}>
            <option value="">—</option>
            {(servers.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
        </Field>
        <Field
          label={`${t('common.project')} (${t('common.optional')})`}
          hint={
            !server ? (
              t('monitoring.url.pickServerFirst')
            ) : server.projects.length === 0 ? (
              <>
                {t('monitoring.url.serverHasNoProjects')}{' '}
                <Link to={`/monitoring/servers/${server.id}/edit`} className="underline hover:text-[var(--text-primary)]">
                  {t('monitoring.url.addProjectsOnServer')}
                </Link>
              </>
            ) : (
              t('monitoring.url.projectHint')
            )
          }
        >
          <Select
            value={form.projectId}
            disabled={!server || projectChoices.length === 0}
            onChange={(e) => setForm({ ...form, projectId: e.target.value })}
          >
            <option value="">{t('monitoring.url.noProject')}</option>
            {projectChoices.map((p) => (
              <option key={p.id} value={p.id}>{`${p.code} · ${localized(p)}`}</option>
            ))}
          </Select>
        </Field>

        <Field label={t('monitoring.url.kind')} required>
          <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as MonitorKind })}>
            <option value="HTTP">{t(kindLabel('HTTP'))}</option>
            <option value="TCP">{t(kindLabel('TCP'))}</option>
          </Select>
        </Field>
        <Field label={t('common.name')} required>
          <Input
            value={form.name}
            maxLength={120}
            placeholder={isHttp ? 'VMOS admin' : 'PostgreSQL'}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus={!!form.serverId && !editing}
          />
        </Field>

        {isHttp ? (
          <>
            <Field label={t('monitoring.url.url')} required hint={t('monitoring.url.urlHint')} className="sm:col-span-2">
              <Input
                dir="ltr"
                value={form.url}
                maxLength={2048}
                placeholder="https://admin.example.com/health"
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                onBlur={nameFromAddress}
              />
            </Field>
            <Field label={t('monitoring.url.method')}>
              <Select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value as MonitorMethod })}>
                <option value="GET">GET</option>
                <option value="HEAD">HEAD</option>
                <option value="POST">POST</option>
              </Select>
            </Field>
            <Field label={t('monitoring.url.expectedStatus')} hint={t('monitoring.url.expectedStatusHint')}>
              <Input
                dir="ltr"
                value={form.expectedStatus}
                maxLength={100}
                placeholder={t('monitoring.url.any2xx')}
                onChange={(e) => setForm({ ...form, expectedStatus: e.target.value })}
              />
            </Field>
            <Field label={t('monitoring.url.keyword')} hint={t('monitoring.url.keywordHint')} className="sm:col-span-2">
              <Input value={form.keyword} maxLength={200} onChange={(e) => setForm({ ...form, keyword: e.target.value })} />
            </Field>
            <Field label={t('monitoring.url.headers')} hint={t('monitoring.url.headersHint')} className="sm:col-span-2">
              <Textarea
                dir="ltr"
                className="font-mono text-mono-sm"
                value={form.headers}
                placeholder={'Accept: application/json\nAuthorization: Bearer …'}
                onChange={(e) => setForm({ ...form, headers: e.target.value })}
              />
            </Field>
            {form.method === 'POST' && (
              <Field label={t('monitoring.url.body')} hint={t('monitoring.url.bodyHint')} className="sm:col-span-2">
                <Textarea
                  dir="ltr"
                  className="font-mono text-mono-sm"
                  value={form.body}
                  maxLength={10_000}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                />
              </Field>
            )}
          </>
        ) : (
          <>
            <Field label={t('monitoring.url.host')} required hint={t('monitoring.url.hostHint')}>
              <Input
                dir="ltr"
                value={form.host}
                maxLength={253}
                placeholder="127.0.0.1"
                onChange={(e) => setForm({ ...form, host: e.target.value })}
              />
            </Field>
            <Field label={t('monitoring.url.port')} required>
              <Input
                dir="ltr"
                type="number"
                min={1}
                max={65535}
                value={form.port}
                placeholder="5432"
                onChange={(e) => setForm({ ...form, port: e.target.value })}
              />
            </Field>
          </>
        )}

        <Field label={t('monitoring.url.severity')} hint={t('monitoring.url.severityHint')}>
          <Select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value as MonitorSeverity })}>
            {MONITOR_SEVERITIES.map((s) => (
              <option key={s} value={s}>{t(severityLabel(s))}</option>
            ))}
          </Select>
        </Field>

        <p className="mt-2 border-t hairline pt-3 text-body-sm font-medium text-[var(--text-secondary)] sm:col-span-2">
          {t('monitoring.url.checks')}
          <span className="ms-2 text-caption font-normal text-faint">{t('monitoring.url.checksHint')}</span>
        </p>

        <Field label={t('monitoring.url.interval')}>
          <Select value={figures.intervalSeconds} onChange={(e) => setFigures({ ...figures, intervalSeconds: e.target.value })}>
            <option value="">
              {d ? t('monitoring.url.defaultOption', { value: intervalText(d.intervalSeconds, t) }) : t('monitoring.url.default')}
            </option>
            {(d?.intervalChoices ?? []).map((seconds) => (
              <option key={seconds} value={String(seconds)}>{intervalText(seconds, t)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('monitoring.url.timeout')}>
          <Input
            type="number"
            min={d?.limits.timeoutSeconds.min}
            max={d?.limits.timeoutSeconds.max}
            value={figures.timeoutSeconds}
            placeholder={placeholder(d?.timeoutSeconds, (v) => t('monitoring.url.seconds', { n: v }))}
            onChange={(e) => setFigures({ ...figures, timeoutSeconds: e.target.value })}
          />
        </Field>
        <Field label={t('monitoring.url.failures')} hint={t('monitoring.url.failuresHint')}>
          <Input
            type="number"
            min={d?.limits.failuresToConfirm.min}
            max={d?.limits.failuresToConfirm.max}
            value={figures.failuresToConfirm}
            placeholder={placeholder(d?.failuresToConfirm, (v) => formatNumber(v, locale))}
            onChange={(e) => setFigures({ ...figures, failuresToConfirm: e.target.value })}
          />
        </Field>
        {isHttp && (
          <>
            <Field label={t('monitoring.url.slowMs')}>
              <Input
                type="number"
                min={d?.limits.slowMs.min}
                max={d?.limits.slowMs.max}
                step={100}
                value={figures.slowMs}
                placeholder={placeholder(d?.slowMs, (v) => t('monitoring.url.ms', { n: formatNumber(v, locale) }))}
                onChange={(e) => setFigures({ ...figures, slowMs: e.target.value })}
              />
            </Field>
            <Field label={t('monitoring.url.sslWarnDays')} hint={t('monitoring.url.sslWarnDaysHint')}>
              <Input
                type="number"
                min={d?.limits.sslWarnDays.min}
                max={d?.limits.sslWarnDays.max}
                value={figures.sslWarnDays}
                placeholder={placeholder(d?.sslWarnDays, (v) => t('monitoring.url.days', { n: v }))}
                onChange={(e) => setFigures({ ...figures, sslWarnDays: e.target.value })}
              />
            </Field>
          </>
        )}
      </FormGrid>
    </FormPage>
  );
}
